import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, Mail, Lock, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// BLOCK 170 ,  the single shared step-1 gate used by all three submit pages
// (SubmitBusiness / SubmitLand / SubmitMultifamily). Anonymous submission
// has been removed: a visitor must verify their email (new account) or log
// in with email + password (existing account) before step 2 (the form,
// passed as `children`) ever renders. Passwordless login is never allowed
// anywhere ,  the 6-digit code only proves inbox ownership; it is never a
// substitute for a password.
const ADMIN_TIER_ROLES = ["admin", "super_admin", "manager"];
const RESEND_COOLDOWN = 60;

function CodeInput({ value, onChange, testid }) {
  return (
    <input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
      inputMode="numeric"
      maxLength={6}
      placeholder="000000"
      className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2.5 text-center text-2xl tracking-[0.5em] font-mono focus:outline-none focus:border-emerald-500/60"
    />
  );
}

function AdminNotice() {
  return (
    <div className="ae-card p-6 flex items-start gap-3" data-testid="seller-gate-admin-notice">
      <ShieldAlert className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
      <div>
        <div className="font-heading text-lg">Admin accounts cannot submit here.</div>
        <p className="text-sm text-slate-400 mt-1">
          Staff accounts use internal intake instead of the public submission form.
        </p>
      </div>
    </div>
  );
}

export function VerifyExistingPanel() {
  const { user, verifyEmailWithTicket } = useAuth();
  const [stage, setStage] = useState("intro");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [cooldown]);

  const sendCode = async () => {
    setBusy(true);
    try {
      const r = await api.post("/verify/send-code", { email: user.email });
      if (r.data.ok === false) {
        toast.error(r.data.message);
      } else {
        toast.success("Code sent to your email");
        setStage("code");
        setCooldown(RESEND_COOLDOWN);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send code");
    } finally {
      setBusy(false);
    }
  };

  const confirmAndVerify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/verify/confirm-code", { email: user.email, code });
      await verifyEmailWithTicket(r.data.ticket);
      toast.success("Email verified");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ae-card p-6" data-testid="seller-gate-verify-existing">
      <div className="flex items-center gap-2 mb-2">
        <Mail className="h-4 w-4 text-emerald-400" />
        <div className="font-heading text-lg">Verify your email to continue.</div>
      </div>
      <p className="text-sm text-slate-400 mb-4">
        We need to confirm you have access to {user?.email} before you can submit a listing.
      </p>
      {stage === "intro" ? (
        <button onClick={sendCode} disabled={busy} data-testid="seller-gate-verify-send" className="btn-primary text-sm">
          {busy ? "Sending…" : "Send verification code"}
        </button>
      ) : (
        <div className="space-y-3 max-w-xs">
          <CodeInput value={code} onChange={setCode} testid="seller-gate-verify-code-input" />
          <button onClick={confirmAndVerify} disabled={busy || code.length !== 6} data-testid="seller-gate-verify-confirm" className="btn-primary w-full text-sm">
            {busy ? "Verifying…" : "Verify"}
          </button>
          <button onClick={sendCode} disabled={busy || cooldown > 0} data-testid="seller-gate-verify-resend" className="text-xs text-slate-500 hover:text-slate-300 disabled:opacity-40">
            {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
          </button>
        </div>
      )}
    </div>
  );
}

function AnonymousAuthFlow() {
  const { login, createSellerAccount } = useAuth();
  const [stage, setStage] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [ticket, setTicket] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [cooldown]);

  const sendCode = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/verify/send-code", { email });
      if (r.data.ok === false) {
        toast.error(r.data.message);
      } else {
        toast.success("Code sent. Check your inbox.");
        setStage("code");
        setCooldown(RESEND_COOLDOWN);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send code");
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/verify/confirm-code", { email, code });
      setTicket(r.data.ticket);
      setStage(r.data.account_exists ? "password" : "create");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect code");
    } finally {
      setBusy(false);
    }
  };

  const loginExisting = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password, { verification_ticket: ticket });
      toast.success("Welcome back");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Incorrect password");
    } finally {
      setBusy(false);
    }
  };

  const createAccount = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await createSellerAccount({ email, ticket, name, password });
      toast.success("Account created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ae-card p-6 max-w-md" data-testid="seller-gate-anonymous">
      <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Step 1 of 2</div>
      <h2 className="font-heading text-xl mb-4">Sign in or create a seller account to submit a listing.</h2>

      {stage === "email" && (
        <form onSubmit={sendCode} className="space-y-3">
          <input
            data-testid="seller-gate-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500/60"
          />
          <button type="submit" disabled={busy} data-testid="seller-gate-send-code-btn" className="btn-primary w-full text-sm">
            {busy ? "Sending…" : "Send verification code"}
          </button>
        </form>
      )}

      {stage === "code" && (
        <form onSubmit={confirmCode} className="space-y-3">
          <p className="text-sm text-slate-400">Enter the 6-digit code sent to {email}.</p>
          <CodeInput value={code} onChange={setCode} testid="seller-gate-code-input" />
          <button type="submit" disabled={busy || code.length !== 6} data-testid="seller-gate-confirm-code-btn" className="btn-primary w-full text-sm">
            {busy ? "Verifying…" : "Verify code"}
          </button>
          <button type="button" onClick={sendCode} disabled={busy || cooldown > 0} data-testid="seller-gate-resend-btn" className="text-xs text-slate-500 hover:text-slate-300 disabled:opacity-40">
            {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
          </button>
        </form>
      )}

      {stage === "password" && (
        <form onSubmit={loginExisting} className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-slate-300 mb-1">
            <Lock className="h-3.5 w-3.5 text-emerald-400" /> {email} ,  enter your password to sign in.
          </div>
          <input
            data-testid="seller-gate-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500/60"
          />
          <button type="submit" disabled={busy} data-testid="seller-gate-login-btn" className="btn-primary w-full text-sm">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      )}

      {stage === "create" && (
        <form onSubmit={createAccount} className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-slate-300 mb-1">
            <UserPlus className="h-3.5 w-3.5 text-emerald-400" /> {email} verified. Set up your seller account.
          </div>
          <input
            data-testid="seller-gate-create-name-input"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500/60"
          />
          <input
            data-testid="seller-gate-create-password-input"
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Choose a password"
            className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500/60"
          />
          <button type="submit" disabled={busy} data-testid="seller-gate-create-submit-btn" className="btn-primary w-full text-sm">
            {busy ? "Creating…" : "Create account and continue"}
          </button>
        </form>
      )}
    </div>
  );
}

export function SellerAuthGate({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="text-slate-500 text-sm" data-testid="seller-gate-loading">Loading…</div>;
  }
  if (user) {
    if (ADMIN_TIER_ROLES.includes(user.role)) return <AdminNotice />;
    if (!user.email_verified_at) return <VerifyExistingPanel />;
    return children;
  }
  return <AnonymousAuthFlow />;
}
